/**
 * Tracking module singleton. The SDK's initializeTrackingModule() is intended
 * to be called once per JS bundle (re-init would create duplicate Segment
 * instances), and identify() should fire once per merchant session (re-fire
 * duplicates traffic). Both are kept in module-level state below.
 */
import {
  initializeTrackingModule,
  isTestEnvironment,
  TrackableProperties,
  TrackingModule,
} from '@lightspeed-payments/payments-portal-sdk';

import { getMerchantDetails } from './getMerchantDetails';
import { getTrackingEnvironment } from './getTrackingEnvironment';

let modulePromise: Promise<TrackingModule> | null = null;
let identified = false;

export const getTrackingModule = (): Promise<TrackingModule> | null => {
  if (isTestEnvironment()) return null;
  if (!modulePromise) {
    modulePromise = initializeTrackingModule({
      configs: [{ name: 'capital', environment: getTrackingEnvironment() }],
    }).catch((err) => {
      modulePromise = null;
      throw err;
    });
  }
  return modulePromise;
};

const ensureIdentify = (m: TrackingModule) => {
  if (identified) return;
  identified = true;
  const { pui, merchantName, pli, userId, accountId, roleName } = getMerchantDetails();
  const identifyProps: TrackableProperties = {
    merchant_name: merchantName,
    pli,
    user_id: userId,
    product_id: 'r-series',
    merchant_id: accountId,
    role_name: roleName,
  };
  m.identify(pui, identifyProps);
};

const getCommonProps = (eventProps: TrackableProperties): TrackableProperties => {
  const { pli, merchantName } = getMerchantDetails();
  return {
    ...eventProps,
    merchant_name: merchantName,
    pli,
    product_id: 'r-series',
    surface: 'r-series',
  };
};

export const track = async (eventName: string, eventProps: TrackableProperties): Promise<void> => {
  try {
    const m = await getTrackingModule();
    if (!m) return;
    ensureIdentify(m);
    m.trackEvent(eventName, getCommonProps(eventProps));
  } catch {
    // tracking failures must never propagate to the UI
  }
};

export const trackAsync = async (
  eventName: string,
  eventProps: TrackableProperties
): Promise<void> => {
  const m = await getTrackingModule();
  if (!m) return;
  ensureIdentify(m);
  await new Promise<void>((resolve) => {
    m.trackEvent(eventName, getCommonProps(eventProps), () => resolve());
  });
};

// eslint-disable-next-line no-underscore-dangle
export const __resetForTests = (): void => {
  if (process.env.NODE_ENV !== 'test') return;
  modulePromise = null;
  identified = false;
};
